import { useEffect, useState } from 'react';
import { Sparkles, Brain, Zap, Calculator, CheckCircle2 } from 'lucide-react';
import { ProgressBar } from './ProgressBar';

const STATUS_MESSAGES = [
  { text: "Thinking ahead...", icon: Brain },
  { text: "Consulting with the vitamin gods...", icon: Sparkles },
  { text: "Calculating optimal timing...", icon: Calculator },
  { text: "Organizing your wellness journey...", icon: Zap },
  { text: "Aligning your supplements with the cosmos...", icon: Sparkles },
  { text: "Optimizing for maximum absorption...", icon: Brain },
  { text: "Checking for timing conflicts...", icon: Calculator },
  { text: "Fine-tuning your schedule...", icon: Zap },
  { text: "Adding a sprinkle of magic...", icon: Sparkles },
  { text: "Almost there...", icon: CheckCircle2 },
];

interface PlanGenerationModalProps {
  isVisible: boolean;
  isRecompute?: boolean;
}

export function PlanGenerationModal({ isVisible, isRecompute = false }: PlanGenerationModalProps) {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsAnimatingIn(true);
      setProgress(0);
      setCurrentMessageIndex(0);

      const progressInterval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 95) return prev;
          const increment = Math.random() * 15 + 5;
          return Math.min(95, prev + increment);
        });
      }, 300);

      const messageInterval = setInterval(() => {
        setCurrentMessageIndex(prev => (prev + 1) % STATUS_MESSAGES.length);
      }, 1500);

      return () => {
        clearInterval(progressInterval);
        clearInterval(messageInterval);
      };
    } else {
      setIsAnimatingIn(false);
    }
  }, [isVisible]);

  if (!isVisible) return null;

  const currentMessage = STATUS_MESSAGES[currentMessageIndex];
  const Icon = currentMessage.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm animate-fade-in">
      <div
        className={`bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 ${
          isAnimatingIn ? 'animate-scale-in' : ''
        }`}
      >
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-400 to-yellow-500 rounded-full mb-4 animate-pulse-slow">
            <Icon className="text-white" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {isRecompute ? 'Recomputing Your Plan' : 'Creating Your Plan'}
          </h2>
        </div>

        <div className="mb-6">
          <div
            key={currentMessageIndex}
            className="flex items-center justify-center gap-2 text-gray-600 animate-fade-in min-h-[24px]"
          >
            <Icon size={18} className="text-orange-500 animate-pulse" />
            <span className="font-medium">{currentMessage.text}</span>
          </div>
        </div>

        <ProgressBar progress={progress} />

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-500">
            {progress < 30 && "Loading your schedule..."}
            {progress >= 30 && progress < 60 && "Expanding groups and calculating times..."}
            {progress >= 60 && progress < 85 && "Applying gap rules and resolving conflicts..."}
            {progress >= 85 && "Finalizing your perfect plan..."}
          </p>
        </div>
      </div>
    </div>
  );
}
